import { Signal } from '@verlo/shared/components/ui/signal';

const when = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};
const METHOD = { cash: 'Cash', card: 'Card', voucher: 'Voucher', other: 'Other' };

const Row = ({ label, value, sub, strong, sign, testId }) => (
  <div className={`flex items-baseline justify-between gap-3 py-1 text-[13.5px] ${strong ? 'font-semibold text-ink' : 'text-ink-soft'}`} data-testid={testId}>
    <span className="min-w-0">{label}{sub && <span className="ml-1.5 text-[11px] text-ink-faint">{sub}</span>}</span>
    <span className="shrink-0 font-mono tabular-nums">{sign}{value}</span>
  </div>
);

const Block = ({ title, children }) => (
  <section className="break-inside-avoid">
    <p className="font-heading text-[10.5px] font-bold uppercase tracking-[0.06em] text-ink-faint">{title}</p>
    <div className="mt-1 divide-y divide-hairline border-y border-hairline">{children}</div>
  </section>
);

/**
 * The Z read: one closed session, frozen at close. Takings by method, card tips (tronc,
 * their own line), refunds, discounts, comps, voids; the drawer (expected, counted,
 * variance and its reason); the PDQ reconciliation; tips out, float carried, to the
 * safe; who opened and who signed off. Prints as a receipt-shaped sheet (`.z-read`).
 */
const ZRead = ({ session: s, formatCurrency, compact = false }) => {
  if (!s) return null;
  const sum = s.summary || {};
  const exp = s.expected_lines || s.expected || {};
  const money = (v) => formatCurrency(v || 0);
  const closed = s.status === 'closed';
  const v = s.variance ?? 0;
  const pv = s.pdq_variance;
  return (
    <div className={`z-read space-y-4 ${compact ? 'text-[13px]' : ''}`} data-testid="z-read" data-session-id={s.id}>
      <header className="flex items-start justify-between gap-3">
        <div>
          <p className="font-heading text-[10.5px] font-bold uppercase tracking-[0.06em] text-ink-faint">{closed ? `Z read · ${s.z_number ?? '—'}` : 'X read · session open'}</p>
          <h2 className="mt-0.5 text-[18px] font-bold leading-tight text-ink">{s.terminal_label || s.terminal_id} · {s.trading_date}</h2>
          <p className="mt-0.5 text-[12px] text-ink-faint">
            Opened {when(s.opened_at)}{s.opened_by_name ? ` by ${s.opened_by_name}` : ''}
            {closed && <> · Signed off {when(s.closed_at)}{s.closed_by_name ? ` by ${s.closed_by_name}` : ''}</>}
          </p>
        </div>
        {closed && (
          <Signal tone={v === 0 ? 'ok' : Math.abs(v) <= 5 ? 'warn' : 'stop'} data-testid="z-read-variance">
            {v === 0 ? 'Balanced' : v > 0 ? `Over ${money(v)}` : `Short ${money(-v)}`}
          </Signal>
        )}
      </header>

      <div className={`grid gap-4 ${compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
        <div className="space-y-4">
          <Block title="Takings">
            {Object.entries(sum.takings || {}).map(([m, val]) => <Row key={m} label={METHOD[m] || m} value={money(val)} />)}
            <Row label="Total takings" value={money(sum.takings_total)} strong testId="z-read-takings" />
            {sum.revenue_by_line && ['food', 'drink', 'other'].filter((k) => (sum.revenue_by_line[k] || 0) > 0).map((k) => (
              <Row key={k} label={`Sales · ${k}`} value={money(sum.revenue_by_line[k])} testId={`z-read-line-${k}`} />
            ))}
            {(sum.typed_extras?.count || 0) > 0 && <Row label="Typed extras" sub={`${sum.typed_extras.count} priced by hand`} value={money(sum.typed_extras.value)} testId="z-read-typed" />}
            {(sum.card_tips || 0) > 0 && <Row label="Card tips" sub="tronc · not till income" value={money(sum.card_tips)} testId="z-read-card-tips" />}
            {(sum.cash_tips || 0) > 0 && <Row label="Cash tips" value={money(sum.cash_tips)} />}
          </Block>
          <Block title="Adjustments">
            {Object.entries(sum.refunds || {}).map(([m, val]) => <Row key={m} label={`Refunds · ${METHOD[m] || m}`} value={money(val)} sign="−" />)}
            <Row label="Discounts" value={money(sum.discounts)} sign="−" />
            <Row label="Comps" value={money(sum.comps)} sign="−" />
            <Row label="Voids" sub={sum.voids?.count ? `${sum.voids.count}` : ''} value={money(sum.voids?.value)} />
            <Row label="Orders · covers" value={`${sum.orders ?? 0} · ${sum.covers ?? 0}`} />
          </Block>
        </div>

        <div className="space-y-4">
          <Block title="The drawer">
            <Row label="Opening float" value={money(exp.float_opening)} />
            <Row label="Cash sales" value={money(exp.cash_sales)} sign="+" />
            {(exp.cash_tips || 0) > 0 && <Row label="Cash tips taken" value={money(exp.cash_tips)} sign="+" />}
            {(exp.float_top_ups || 0) > 0 && <Row label="Float top-ups" value={money(exp.float_top_ups)} sign="+" />}
            {(exp.cash_refunds || 0) > 0 && <Row label="Cash refunds" value={money(exp.cash_refunds)} sign="−" />}
            {(exp.paid_outs || 0) > 0 && <Row label="Paid out" value={money(exp.paid_outs)} sign="−" />}
            {(exp.safe_drops || 0) > 0 && <Row label="Safe drops" value={money(exp.safe_drops)} sign="−" />}
            {(exp.tips_out || 0) > 0 && <Row label="Tips out" value={money(exp.tips_out)} sign="−" />}
            <Row label="Expected" value={money(exp.expected)} strong testId="z-read-expected" />
            {closed && <Row label="Counted" value={money(s.declared_cash)} strong testId="z-read-declared" />}
            {closed && v !== 0 && <Row label={v > 0 ? 'Over' : 'Short'} value={money(Math.abs(v))} strong />}
          </Block>
          {closed && s.variance_reason && (
            <p className="text-[12.5px] text-ink-soft"><span className="font-semibold text-ink">Reason · </span>{s.variance_reason}</p>
          )}
          {closed && (
            <Block title="Card · PDQ">
              <Row label="Card takings on the till" value={money(s.card_takings)} />
              <Row label="PDQ terminal total" value={s.pdq_total == null ? 'not entered' : money(s.pdq_total)} testId="z-read-pdq" />
              {pv != null && <Row label={pv === 0 ? 'PDQ matches' : pv > 0 ? 'PDQ over' : 'PDQ short'} value={pv === 0 ? '' : money(Math.abs(pv))} strong />}
            </Block>
          )}
          {closed && (
            <Block title="Where the cash went">
              <Row label="Float carried" value={money(s.float_carried)} />
              <Row label="To the safe" value={money(s.to_safe)} strong testId="z-read-to-safe" />
            </Block>
          )}
          {s.denominations && (
            <Block title="The count">
              {Object.entries(s.denominations).filter(([, n]) => n > 0).map(([d, n]) => <Row key={d} label={d} sub={`× ${n}`} value="" />)}
            </Block>
          )}
          {s.notes && <p className="text-[12.5px] text-ink-soft"><span className="font-semibold text-ink">Notes · </span>{s.notes}</p>}
        </div>
      </div>
    </div>
  );
};

export default ZRead;
